import { EditOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { App, Modal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { useEffect } from 'react';
import { SpecimenGroup, type SpecimenUpdateInput } from '../../../../graphql/generated/graphql.ts';
import type { Form, Specimen, TestTube } from '../Specimens.tsx';
import { SpecimenForm } from './SpecimenForm.tsx';
import { UPDATE_SPECIMEN_MUTATION } from './UpdateSpecimenModal.gql';

export const UpdateSpecimenModal: React.FC<{
  specimen: Specimen | null;
  specimens: Specimen[];
  testTubes: TestTube[];
  forms: Form[];
  analysisStations: { id: string; name: string }[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ specimen, specimens, testTubes, forms, analysisStations, onClose, onSuccess }) => {
  const [updateSpecimenMutation, { loading }] = useMutation(UPDATE_SPECIMEN_MUTATION);
  const [form] = useForm<SpecimenUpdateInput>();
  const { message } = App.useApp();

  const update = async (values: SpecimenUpdateInput) => {
    try {
      await updateSpecimenMutation({
        variables: {
          id: specimen?.id ?? '',
          input: { ...values },
        },
      });

      message.success('Material wurde aktualisiert');
      onSuccess();
      closeAndReset();
    } catch (_e) {
      message.error('Material konnte nicht aktualisiert werden');
    }
  };

  const closeAndReset = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    form.setFieldsValue({
      name: specimen?.name ?? '',
      classification: specimen?.classification ?? '',
      analysisStationIds: specimen?.analysisStations.map(s => s.id) ?? [],
      testTubeIds: specimen?.testTubes.map(tt => tt.id) ?? [],
      formIds: specimen?.forms.map(it => it.id) ?? [],
      group: specimen?.group ?? SpecimenGroup.NONE,
      features: specimen?.features ?? [],
      storagePeriod: specimen?.storagePeriod ?? 0,
      requiresLocalizations: specimen?.requiresLocalizations ?? false,
      localizationInputEnabled: specimen?.localizationInputEnabled ?? false,
      predefinedLocalizations: specimen?.predefinedLocalizations ?? [],
      // strip __typename so the value can go straight back into the mutation input
      localizationFields:
        specimen?.localizationFields.map(({ label, inputType, unit, mandatory }) => ({
          label,
          inputType,
          unit,
          mandatory,
        })) ?? [],
    });
  }, [form, specimen]);

  return (
    <Modal
      width={768}
      title="Material bearbeiten"
      okText="Aktualisieren"
      okButtonProps={{ icon: <EditOutlined /> }}
      open={!!specimen}
      onCancel={closeAndReset}
      onOk={form.submit}
      confirmLoading={loading}
      destroyOnHidden
      forceRender
    >
      <SpecimenForm
        form={form}
        onFinish={update}
        nameValidator={name => !specimens.filter(s => s.id !== specimen?.id).some(s => s.name === name)}
        testTubes={testTubes}
        forms={forms}
        analysisStations={analysisStations}
      />
    </Modal>
  );
};
